import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../withTypes';
import { Status, STATUSES } from '@/types/store';
import { Note, RefDocTask } from '@/types/assignmentsGeneral';
import { BookmarkTask } from '@/types/tasks';
import {
  BOOKMARK_TASK_MUTATION,
  UPSERT_TASK_NOTE,
  REFDOC_TASKS_QUERY,
  REFDOC_COMPLETE_TASK_MUTATION,
} from '@/graphql/queries/tasks';
import { RootState } from '..';
import { graphqlRequest, graphqlMutation, addRejectedToast, showRejectedToast } from '../../api/graphqlClient';
import { errorMessages } from '@/utils/errorMessages';

interface FetchRefDocTasks {
  assignmentId: number;
}

interface BookmarkRefDocTask {
  taskId: number;
  isBookmarked: boolean;
}

interface CompleteRefDocTask {
  taskId: number;
  sentenceText: string;
}

type CompleteRefDocTaskPayload = Pick<
  RefDocTask,
  'id' | 'isCompleted' | 'translatedSentence'
> & {
  assignment_id: number;
  assignment_status_id: number;
};

interface UpsertRefDocNote {
  taskId: number;
  userId: number;
  noteText: string;
  noteId?: number | null;
}

export interface RefDocState {
  tasks: RefDocTask[];
  selectedTaskId?: number;
  status: Status;
}

const initialState: RefDocState = {
  tasks: [],
  selectedTaskId: undefined,
  status: STATUSES.IDLE,
};

/* THUNKS */
export const fetchRefDocTasks = createAppAsyncThunk(
  'tasks/fetchRefDocTasks',
  async ({ assignmentId }: FetchRefDocTasks) => {
    const response = await graphqlRequest<FetchRefDocTasks, { refdocTasks: RefDocTask[] }>(
      REFDOC_TASKS_QUERY,
      { assignmentId },
      'no-cache',
    );

    return response.refdocTasks;
  },
);

export const bookmarkRefDocTask = createAppAsyncThunk(
  'tasks/bookmarkRefDocTask',
  async ({ taskId, isBookmarked }: BookmarkRefDocTask) => {
    const response = await graphqlMutation<BookmarkRefDocTask, { bookmarkTask: BookmarkTask }>(
      BOOKMARK_TASK_MUTATION,
      { taskId, isBookmarked },
    );

    return response.bookmarkTask;
  },
);

export const completeRefDocTask = createAppAsyncThunk(
  'tasks/completeRefDocTask',
  async ({ taskId, sentenceText }: CompleteRefDocTask) => {
    const response = await graphqlMutation<CompleteRefDocTask, { completeRefdocTask: CompleteRefDocTaskPayload }>(
      REFDOC_COMPLETE_TASK_MUTATION,
      { taskId, sentenceText },
    );

    return response.completeRefdocTask;
  },
);

export const upsertRefDocNote = createAppAsyncThunk(
  'tasks/upsertRefDocNote',
  async ({ taskId, userId, noteText, noteId }: UpsertRefDocNote) => {
    const response = await graphqlMutation<UpsertRefDocNote, { upsertTaskNote: Note & { taskId: number } }>(
      UPSERT_TASK_NOTE,
      { taskId, userId, noteText, noteId },
    );

    const { upsertTaskNote } = response;

    return {
      ...upsertTaskNote,
      taskId,
    };
  },
);

export const refDocSlice = createSlice({
  name: 'RefDoc',
  initialState,
  reducers: {
    setSelectedTaskId: (state, action: PayloadAction<number | undefined>) => {
      state.selectedTaskId = action.payload;
    },

    selectNextTaskId: (
      state,
      action: PayloadAction<{ selectedTaskId: number, taskListToUse: RefDocTask[] }>,
    ) => {
      const { selectedTaskId, taskListToUse } = action.payload;

      const taskIndex = state.tasks.findIndex(task => task.id === selectedTaskId);
      if (taskIndex >= 0 && state.tasks[taskIndex].translatedSentence?.trim()) {
        state.tasks[taskIndex].isCompleted = true;
      }

      const selectedIndex = taskListToUse.findIndex(task => task.id === selectedTaskId);

      if (taskListToUse.length === 1) {
        state.selectedTaskId = taskListToUse[0].id;
        return;
      }

      if (selectedIndex === taskListToUse.length - 1) {
        state.selectedTaskId = taskListToUse[0].id;
        return;
      }

      if (selectedIndex >= 0 && taskListToUse[selectedIndex + 1]) {
        state.selectedTaskId = taskListToUse[selectedIndex + 1].id;
      }
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchRefDocTasks.pending, (state) => {
      state.status = STATUSES.PENDING;
    });
    builder.addCase(fetchRefDocTasks.fulfilled, (state, action) => {
      state.tasks = action.payload;
      state.selectedTaskId
        = action.payload.find((task: RefDocTask) => !task.isCompleted)?.id
          ?? action.payload[0]?.id
          ?? null;
      state.status = STATUSES.FULFILLED;
    });
    builder.addCase(fetchRefDocTasks.rejected, (state, action) => {
      state.tasks = initialState.tasks;
      state.status = STATUSES.REJECTED;
      state.selectedTaskId = initialState.selectedTaskId;
      showRejectedToast(action, errorMessages.fetchingTasks);
    });

    // TODO: add spinner and additional store status to handle mutating task bookmarks?
    //
    // builder.addCase(bookmarkRefDocTask.pending, (state) => {
    //   state.status = STATUSES.PENDING;
    // });
    builder.addCase(bookmarkRefDocTask.fulfilled, (state, action) => {
      const tasks = state.tasks;
      const taskIndex = tasks.findIndex(task => task.id === action.payload.id);

      if (taskIndex >= 0) {
        tasks[taskIndex].isBookmarked = action.payload.is_bookmarked;
      }
      state.status = STATUSES.FULFILLED;
    });
    addRejectedToast(builder, bookmarkRefDocTask, errorMessages.bookmarkingTask);

    // TODO: add spinner and additional store status to handle mutating completed tasks?
    //
    // builder.addCase(completeRefDocTask.pending, (state) => {
    //   state.status = STATUSES.PENDING;
    // });
    builder.addCase(completeRefDocTask.fulfilled, (state, action) => {
      const tasks = state.tasks;
      const taskIndex = tasks.findIndex(task => task.id === action.payload.id);

      if (taskIndex >= 0) {
        tasks[taskIndex].isCompleted = action.payload.isCompleted;
        tasks[taskIndex].translatedSentence = action.payload.translatedSentence;
      }
      state.status = STATUSES.FULFILLED;
    });
    addRejectedToast(builder, completeRefDocTask, errorMessages.completingTask);

    // TODO: add spinner and additional store status to handle mutating completed tasks?
    //
    // builder.addCase(completeRefDocTask.pending, (state) => {
    //   state.status = STATUSES.PENDING;
    // });
    builder.addCase(upsertRefDocNote.fulfilled, (state, action) => {
      const { taskId, ...note } = action.payload;
      const tasks = state.tasks;
      const taskIndex = tasks.findIndex(task => task.id === taskId);

      if (taskIndex >= 0) {
        const noteIndex = tasks[taskIndex].notes.findIndex(taskNote => taskNote.id === note.id);

        if (noteIndex < 0) {
          tasks[taskIndex].notes.push(note);
        } else {
          tasks[taskIndex].notes[noteIndex] = note;
        }
      }
      state.status = STATUSES.FULFILLED;
    });
    addRejectedToast(builder, upsertRefDocNote, errorMessages.savingNote);
  },
});

export const selectTasks = (state: RootState) =>
  state.refDocReducer.tasks;

export const selectCompletedTasks = createSelector([selectTasks], (tasks) => {
  return tasks.filter(({ isCompleted }) => isCompleted);
});

export const selectTaskById = createSelector(
  [selectTasks, (_, selectedTaskId?: number) => selectedTaskId],
  (tasks, selectedTaskId) => {
    return tasks.find(task => task.id === selectedTaskId);
  },
);

export const selectMappedTaskList = createSelector(
  [selectTasks],
  tasks =>
    tasks
      .map(({ id, sourceSentence, title, isCompleted, isBookmarked }) => ({
        id,
        content: sourceSentence,
        title,
        isCompleted,
        isBookmarked,
      })),
);

export const selectIncompleteTasks = createSelector(
  [selectMappedTaskList],
  (tasks) => {
    return tasks.filter(({ isCompleted }) => !isCompleted);
  },
);

export const selectRefDocStatus = (state: RootState) =>
  state.refDocReducer.status;

export const getTaskNotes = createSelector(
  [selectTasks, (_, selectedTaskId: number | null) => selectedTaskId],
  (tasks, selectedTaskId) => {
    return tasks.find((obj: RefDocTask) => obj.id === selectedTaskId)?.notes;
  },
);

export const {
  setSelectedTaskId,
  selectNextTaskId,
} = refDocSlice.actions;

export default refDocSlice.reducer;
